import type { StorefrontFamily, StorefrontVariant } from "../types";

export type OptionGroup = {
  key: string;
  label: string;
  values: string[];
  kind: "colour" | "size" | "text";
};

export type Selection = Record<string, string>;

const colourValues: Record<string, string> = {
  black: "#222222", white: "#f8f8f4", grey: "#8b9091", gray: "#8b9091",
  red: "#c7433c", blue: "#3d6eb5", green: "#3f7a52", yellow: "#e6bc35",
  orange: "#df8140", purple: "#7652a5", pink: "#e48aac", brown: "#79533a",
  beige: "#d5c4a1", cream: "#f2e7c9", silver: "#b7bdc0", gold: "#caa54c",
};

function clean(value: string | null | undefined): string | null {
  const result = value?.trim();
  return result || null;
}

/** Values stay ordered by their canonical source fields / variant label, never a display-only model. */
export function optionValues(variant: StorefrontVariant): string[] {
  const supplied = variant.options?.map(clean).filter((value): value is string => Boolean(value));
  if (supplied?.length) return supplied;
  const label = clean(variant.variantLabel);
  if (!label) return [];
  return label.split(/\s*(?:\/|\||·)\s*/).map((value) => value.trim()).filter(Boolean);
}

function optionKind(values: string[]): OptionGroup["kind"] {
  const lower = values.map((value) => value.toLowerCase());
  if (lower.every((value) => Object.hasOwn(colourValues, value))) return "colour";
  if (lower.every((value) => /^(?:xxs|xs|s|m|l|xl|xxl|\d+(?:\.\d+)?\s?(?:mm|cm|m|in|inch|inches))$/i.test(value))) return "size";
  return "text";
}

function designName(variant: StorefrontVariant): string {
  return variant.cxFacingName?.trim() || variant.title.trim() || variant.sku || variant.id;
}
function designChoice(family: StorefrontFamily, variant: StorefrontVariant): string {
  const name = designName(variant);
  return family.variants.filter((item) => designName(item) === name).length > 1 ? `${name} (${variant.sku || variant.id})` : name;
}

export function optionGroups(family: StorefrontFamily): OptionGroup[] {
  // Duplicate option tuples must never silently select the first physical SKU.
  const tuples = family.variants.map((variant) => JSON.stringify(optionValues(variant)));
  if (new Set(tuples).size < tuples.length) return [{ key: "design", label: "Design", kind: "text", values: family.variants.map((variant) => designChoice(family, variant)) }];
  const widest = Math.max(0, ...family.variants.map((variant) => optionValues(variant).length));
  return Array.from({ length: widest }, (_, index) => {
    const values = [...new Set(family.variants.map((variant) => optionValues(variant)[index]).filter((value): value is string => Boolean(value)))];
    const kind = optionKind(values);
    return { key: `option-${index}`, label: kind === "colour" ? "Colour" : kind === "size" ? "Size" : widest === 1 ? "Option" : `Option ${index + 1}`, values, kind };
  }).filter((group) => group.values.length > 1);
}

function designChoiceForMatch(variant: StorefrontVariant, group: OptionGroup): string {
  const name = designName(variant);
  return group.values.includes(name) ? name : `${name} (${variant.sku || variant.id})`;
}

export function variantMatches(variant: StorefrontVariant, groups: OptionGroup[], selection: Selection): boolean {
  const values = optionValues(variant);
  return groups.every((group) => !selection[group.key] || (group.key === "design" ? designChoiceForMatch(variant, group) : values[Number(group.key.slice(7))]) === selection[group.key]);
}

export function resolveVariant(family: StorefrontFamily, groups: OptionGroup[], selection: Selection): StorefrontVariant | null {
  if (family.variants.length > 1 && groups.length === 0) return null;
  if (!groups.every((group) => selection[group.key])) return null;
  const matches = family.variants.filter((variant) => variantMatches(variant, groups, selection));
  return matches.length === 1 ? matches[0] : null;
}

/** A later option is constrained only by choices before it, so changing colour can reveal valid sizes. */
export function optionAvailable(family: StorefrontFamily, groups: OptionGroup[], selection: Selection, groupIndex: number, value: string): boolean {
  const group = groups[groupIndex];
  const prefix: Selection = { [group.key]: value };
  groups.slice(0, groupIndex).forEach((previous) => { if (selection[previous.key]) prefix[previous.key] = selection[previous.key]; });
  return family.variants.some((variant) => variant.availability === "in_stock" && variantMatches(variant, groups, prefix));
}

export function chooseOption(family: StorefrontFamily, groups: OptionGroup[], selection: Selection, groupIndex: number, value: string): Selection {
  const next: Selection = { ...selection, [groups[groupIndex].key]: value };
  for (let index = groupIndex + 1; index < groups.length; index += 1) {
    const group = groups[index];
    if (next[group.key] && !optionAvailable(family, groups, next, index, next[group.key])) delete next[group.key];
  }
  return next;
}

export function defaultSelection(family: StorefrontFamily, groups: OptionGroup[]): Selection {
  const preferred = family.variants.find((variant) => variant.id === family.defaultVariantId && variant.availability === "in_stock");
  if (!preferred) return {};
  const values = optionValues(preferred);
  return Object.fromEntries(groups.map((group) => [group.key, group.key === "design" ? designChoice(family, preferred) : values[Number(group.key.slice(7))]]).filter(([, value]) => Boolean(value)));
}

export function colourHex(value: string): string | null { return colourValues[value.toLowerCase()] || null; }

export function manageableInCard(family: StorefrontFamily): boolean {
  const groups = optionGroups(family);
  return groups.length > 0 && groups.length <= 2 && groups.every((group) => group.values.length <= 5) && groups.reduce((total, group) => total + group.values.length, 0) <= 8;
}
